/*
  Simple Form UI Component
  Like other simple form components,
  use with field {name: xxx, value: xxx} and change/update handler.
*/

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import 'react-dates/initialize';
import { SingleDatePicker } from 'react-dates';
import moment from 'moment';
import 'react-dates/lib/css/_datepicker.css';

import { CalendarIcon } from 'views/ui/icons';
import { Theme } from 'ui_system/theme';

const DatePicker = styled('div')`
  position: relative;
  .DayPicker__withBorder {
    border-radius: 0;
  }
  .SingleDatePickerInput__withBorder {
    border-radius: 0;
    border: 1px solid ${Theme.colors.borderActionableFieldColor};
    margin: 0;
  }
  .SingleDatePickerInput__disabled {
    background-color: #fff;
  }
  .SingleDatePicker_picker {
    margin-bottom: 30px;
  }
  .DateInput {
    margin: 1px;
    width: 99%;
  }
  .DateInput_input {
    cursor: pointer;
    font-weight: 400;
    font-size: 14px;
    height: 44px;
    @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
      font-size: 13px;
    }
  }
  .DateInput_input:disabled {
    color: #ccc;
    background-color: #fff;
    font-style: normal;
  }
  .DateInput_input__focused {
    border-color: #fff;
  }
  .CalendarDay__selected,
  .CalendarDay__selected:active,
  .CalendarDay__selected:hover {
    background: ${(props) =>
      props.brandColor || Theme.colors.linksDesignElementsBaseColor};
    border: none;
  }
  .CalendarMonth_caption {
    text-align: left;
  }
  .DayPickerNavigation {
    .DayPickerNavigation_button {
      border-radius: 0;
    }
    .DayPickerNavigation_button:first-child {
      left: auto;
      right: 54px;
      border: none;
    }
    .DayPickerNavigation_button:last-child {
      right: 14px;
      border: none;
    }
  }
  .CalendarMonthGrid_month__horizontal {
    margin-left: -10px;
  }
  .DateInput_fang {
    margin-top: -1px;
  }
  .CalendarMonth table {
    border-bottom: none;
  }
`;

const CalendarIcn = styled(CalendarIcon)`
  position: relative;
  left: -25px;
  top: 5px;
  height: 16px;
  fill: ${Theme.colors.iconsNotLinkedColor};
  cursor: pointer;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    position: absolute;
    left: 127px;
    top: 15px;
    height: 16px;
    fill: ${Theme.colors.iconsNotLinkedColor};
  }
`;

const SimpleDate = (props) => {
  const {
    field,
    displayFormat,
    handleDateChange,
    onToggled,
    handleFocus,
    openDirection,
    disabled,
    placeholder,
    availableDates,
    excludedDates,
    isOutsideRange,
    brandColor,
  } = props;

  const [focused, setFocused] = useState(false);

  const onFocusChange = (e = null) => {
    if (disabled) return;

    // Support for icon click focus
    const hasFocus = e ? e.focused : true;

    // Update local state
    setFocused(hasFocus);

    // Call parent handlers
    onToggled(hasFocus);
    if (handleFocus) handleFocus(hasFocus);
  };

  let date = field.value || null;
  if (!moment.isMoment(date) && date !== null) date = moment(date);

  const isOutsideRangeDefault = (dateMoment) => {
    if (excludedDates.includes(dateMoment.format('YYYY-MM-DD'))) {
      return true;
    }

    if (dateMoment.isBefore(moment().startOf('day'))) {
      return true;
    }

    if (availableDates.length === 0) {
      return false;
    }

    return !availableDates.includes(dateMoment.format('YYYY-MM-DD'));
  };

  return (
    <DatePicker brandColor={brandColor}>
      <SingleDatePicker
        date={date}
        placeholder={placeholder}
        daySize={32}
        disabled={disabled}
        displayFormat={displayFormat}
        focused={focused}
        hideKeyboardShortcutsPanel
        id={field.name}
        numberOfMonths={1}
        onDateChange={(dateMoment) =>
          handleDateChange(field.name, dateMoment.format('YYYY-MM-DD'))
        }
        onFocusChange={onFocusChange}
        openDirection={openDirection}
        readOnly
        transitionDuration={0}
        isOutsideRange={isOutsideRange || isOutsideRangeDefault}
      />
      <CalendarIcn
        onClick={() => {
          onFocusChange();
        }}
      />
    </DatePicker>
  );
};

SimpleDate.defaultProps = {
  availableDates: [],
  disabled: false,
  displayFormat: 'MMM D, YYYY',
  excludedDates: [],
  handleFocus: null,
  isOutsideRange: null,
  onToggled: () => {},
  openDirection: 'down',
  placeholder: 'Select',
};

SimpleDate.propTypes = {
  availableDates: PropTypes.arrayOf(PropTypes.string), // array of strings in format 'YYYY-MM-DD'
  disabled: PropTypes.bool,
  displayFormat: PropTypes.string,
  excludedDates: PropTypes.arrayOf(PropTypes.string),
  field: PropTypes.object.isRequired,
  handleDateChange: PropTypes.func.isRequired,
  handleFocus: PropTypes.func,
  isOutsideRange: PropTypes.func, // fn to override default isOutsideRange behavior
  onToggled: PropTypes.func,
  openDirection: PropTypes.string,
  placeholder: PropTypes.string,
};

export default SimpleDate;
